// -----------------------------------------------------------------------------
// YesNoTogglePanel  (YES_NO_TOGGLE)
//
// Mirrors YesNoToggleCell.handleAnswer in TaskChecklistField.jsx. Inline Yes / No
// → does the /complete/ POST itself, then calls onComplete(item) as a refresh
// signal (self-contained pattern, like UploadDocumentPanel).
//
// Endpoint (mirrors YesNoToggleCell.handleAnswer):
//   POST /api/v1/practices/{practiceId}/tasks/{taskId}/checklist/{itemId}/complete/
//   body { completion_state: 'YES', response_payload: { answer: 'yes' | 'no' } }
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState } from 'react';
import { Box, Typography, Button, Stack, CircularProgress } from '@mui/material';
import { dispatch } from 'src/redux/store';
import { updateError } from 'src/redux/slices/user';
import { callApi } from 'src/utils/apiCall';
import Iconify from 'src/components/Iconify';

export default function YesNoTogglePanel({ item, practiceId, onComplete, onClose }) {
    const [submitting, setSubmitting] = useState(false);
    const title = item?.title || item?.name || item?.description || 'Yes or no?';

    const taskId = item?.taskId ?? item?.rawTask?.id;
    const itemId = item?.itemId ?? item?.id;

    const handleAnswer = async (answer) => {
        if (submitting) return;
        if (!practiceId || !taskId || !itemId) {
            onComplete?.(item);
            return;
        }
        setSubmitting(true);
        try {
            const res = await callApi(
                `/api/v1/practices/${practiceId}/tasks/${taskId}/checklist/${itemId}/complete/`,
                'POST',
                { completion_state: 'YES', response_payload: { answer } }
            );
            if (res.ok) {
                onComplete?.(item);
            } else {
                dispatch(updateError({ success: false, message: res?.data?.detail || 'Failed to save — please try again.' }));
            }
        } catch (err) {
            dispatch(updateError({ success: false, message: err?.message || 'Failed to save — please try again.' }));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box
            data-testid="yes-no-toggle-panel"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                px: 3,
                py: 2.5,
                gap: 1.75,
                textAlign: 'center',
            }}
        >
            <Box
                sx={{
                    width: 52,
                    height: 52,
                    borderRadius: '14px',
                    bgcolor: 'rgba(7,141,238,0.10)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Iconify icon="eva:toggle-right-outline" width={26} height={26} sx={{ color: '#078DEE' }} />
            </Box>

            <Box>
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.02em' }}>
                    {title}
                </Typography>
            </Box>

            <Stack direction="row" spacing={1} sx={{ width: '100%', maxWidth: 280 }}>
                <Button
                    data-testid="yes-no-toggle-no"
                    variant="outlined"
                    fullWidth
                    disabled={submitting}
                    onClick={() => handleAnswer('no')}
                    sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600 }}
                >
                    No
                </Button>
                <Button
                    data-testid="yes-no-toggle-yes"
                    variant="contained"
                    disableElevation
                    fullWidth
                    disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
                    onClick={() => handleAnswer('yes')}
                    sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, bgcolor: '#00A76F', '&:hover': { bgcolor: '#007867' }, boxShadow: 'none' }}
                >
                    Yes
                </Button>
            </Stack>

            <Button
                variant="text"
                size="small"
                disabled={submitting}
                onClick={onClose}
                sx={{ textTransform: 'none', fontSize: '13px', color: '#6e6e73' }}
            >
                Cancel
            </Button>
        </Box>
    );
}

YesNoTogglePanel.propTypes = {
    item: PropTypes.object,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onComplete: PropTypes.func,
    onClose: PropTypes.func.isRequired,
};

YesNoTogglePanel.defaultProps = {
    item: null,
    practiceId: null,
    onComplete: null,
};
